import { useState, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import {
  CalendarBlank,
  ChartLineUp,
  ChatCircle,
  CheckSquare,
  CreditCard,
  FolderSimple,
  GearSix,
  House,
  List,
  Megaphone,
  Question,
  type Icon as PhosphorIcon,
} from '@phosphor-icons/react';
import { cx } from '../../lib/cx';
import { initialsOf } from '../../lib/initials';
import { useSession } from '../../lib/session';
import { waitingQuery } from '../../lib/tasks-api';
import { Icon } from '../Icon';
import { Avatar } from '../ui/avatar';
import { BrandLogo } from '../ui/brand-logo';
import { Drawer } from '../ui/dialog';
import { IconButton } from '../ui/icon-button';

interface NavItem {
  id?: 'work';
  label: string;
  icon: PhosphorIcon;
  /** Omitted = the screen is not built yet; the item is shown (per the design) but is not a link. */
  to?: '/subdomain/app' | '/subdomain/app/projects' | '/subdomain/app/settings/profile';
  /** Match any child route (Settings has sub-pages). */
  matchChildren?: boolean;
  soon?: boolean;
}

// Figma "Section 02 · Client" sidebar. Home, Projects and Settings exist so far; the rest arrive with their sprints.
const MAIN_NAV: NavItem[] = [
  { label: 'Home', icon: House, to: '/subdomain/app' },
  { label: 'Projects', icon: FolderSimple, to: '/subdomain/app/projects', matchChildren: true },
  { label: 'Chat', icon: ChatCircle },
  { id: 'work', label: 'Work', icon: CheckSquare },
  { label: 'Lead Gen', icon: Megaphone, soon: true },
];
const BUSINESS_NAV: NavItem[] = [
  { label: 'Insights', icon: ChartLineUp },
  { label: 'Bookings', icon: CalendarBlank },
  { label: 'Plan & billing', icon: CreditCard },
  { label: 'Settings', icon: GearSix, to: '/subdomain/app/settings/profile', matchChildren: true },
  { label: 'Help', icon: Question },
];

const ITEM_BASE =
  'flex h-(--mbm-size-icon-button) items-center gap-(--mbm-space-3) rounded-(--mbm-radius-sm) px-(--mbm-space-3) text-label-large ' +
  'focus-visible:outline-none focus-visible:shadow-(--mbm-shadow-focus-ring)';

function NavRow({ item, onNavigate }: { item: NavItem; onNavigate?: () => void }) {
  // Work is where what the team has sent waits for an answer: the chip is how many are waiting.
  const waiting = useQuery({ ...waitingQuery(), retry: false, enabled: item.id === 'work' }).data?.total ?? 0;
  const content = (
    <>
      <Icon icon={item.icon} size="md" />
      <span className="flex-1">{item.label}</span>
      {item.id === 'work' && waiting > 0 ? (
        <span
          aria-label={`${waiting} waiting for you`}
          className="inline-flex h-(--mbm-space-6) min-w-(--mbm-space-7) items-center justify-center rounded-full bg-tag-blue-bg px-(--mbm-space-2) text-label-small text-tag-blue-text"
        >
          {waiting}
        </span>
      ) : null}
      {item.soon ? (
        <span className="rounded-full border border-border-default px-(--mbm-space-2) text-label-small text-text-secondary">
          Soon
        </span>
      ) : null}
    </>
  );

  if (!item.to) {
    // TODO: link these as their sprints land. Shown disabled so the shell matches the design without dead links.
    return (
      <li>
        <span
          aria-disabled="true"
          title="Coming soon"
          className={cx(ITEM_BASE, 'cursor-not-allowed text-text-tertiary')}
        >
          {content}
        </span>
      </li>
    );
  }
  return (
    <li>
      <Link
        to={item.to}
        onClick={onNavigate}
        activeOptions={{ exact: !item.matchChildren }}
        activeProps={{ className: 'bg-surface-field text-text-primary', 'aria-current': 'page' }}
        inactiveProps={{ className: 'text-text-secondary hover:bg-surface-muted hover:text-text-primary' }}
        className={ITEM_BASE}
      >
        {content}
      </Link>
    </li>
  );
}

function SidebarContent({ onNavigate }: { onNavigate?: () => void }) {
  const { data: session } = useSession();
  return (
    <div className="flex h-full flex-col gap-(--mbm-space-5) p-(--mbm-space-4)">
      <div className="px-(--mbm-space-2) pt-(--mbm-space-2)">
        <BrandLogo />
      </div>

      <nav aria-label="Main" className="flex flex-1 flex-col gap-(--mbm-space-5) overflow-y-auto">
        <ul className="flex flex-col gap-(--mbm-space-1)">
          {MAIN_NAV.map((item) => (
            <NavRow key={item.label} item={item} onNavigate={onNavigate} />
          ))}
        </ul>
        <div className="flex flex-col gap-(--mbm-space-2)">
          <p className="px-(--mbm-space-3) text-label-small text-text-tertiary uppercase">Your business</p>
          <ul className="flex flex-col gap-(--mbm-space-1)">
            {BUSINESS_NAV.map((item) => (
              <NavRow key={item.label} item={item} onNavigate={onNavigate} />
            ))}
          </ul>
        </div>
      </nav>

      {/* TODO(api): the design shows a "This month" allowance panel (posts / reels / website updates, resets on
          the billing date). Those per-plan allowances are not exposed by the API yet, so it is not rendered. */}

      <div className="flex items-center gap-(--mbm-space-3) px-(--mbm-space-2)">
        <Avatar initials={initialsOf(session?.user.name, session?.user.email)} gradient size="md" />
        <div className="flex min-w-0 flex-col">
          <span className="truncate text-label-medium text-text-primary">{session?.user.name ?? ''}</span>
          {/* TODO(api): append the plan name ("Owner · Right Hand Mate") once the plan is available. */}
          <span className="truncate text-body-xsmall text-text-tertiary">Owner</span>
        </div>
      </div>
    </div>
  );
}

/**
 * Client app shell (Figma Section 02): fixed 248px sidebar from `lg`, a top bar with a slide-in menu below it.
 * Page content goes in `children`.
 */
export function ClientShell({ children }: { children: ReactNode }) {
  const [menuOpen, setMenuOpen] = useState(false);
  return (
    <div className="min-h-dvh bg-surface-canvas">
      <aside className="fixed inset-y-0 left-0 z-(--mbm-z-header) hidden w-(--mbm-layout-sidebar-width) border-r border-border-line bg-surface-card lg:block">
        <SidebarContent />
      </aside>

      <header className="flex items-center justify-between border-b border-border-line bg-surface-card px-(--mbm-space-4) py-(--mbm-space-3) lg:hidden">
        <BrandLogo />
        <IconButton icon={List} aria-label="Open menu" variant="ghost" onClick={() => setMenuOpen(true)} />
      </header>
      <Drawer
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        title="Menu"
        side="left"
        className="w-(--mbm-layout-sidebar-width)"
      >
        <SidebarContent onNavigate={() => setMenuOpen(false)} />
      </Drawer>

      <div className="lg:pl-(--mbm-layout-sidebar-width)">{children}</div>
    </div>
  );
}
